import { UploadedFile } from '@shared/types';

export enum FileUploadType {
    AIM_NOTES,
    AIM_DOCS,
    STRAIN_INFORMATION,
    STRAIN_TRANSLATIONS,
    CONSTRUCT,
    PROBE_SEQUENCING,
    ABI_FILE_IMPORT,
}

const WARNING_FILE_TYPE = [
    { extension: '.zip', mime: ['application/zip', 'application/x-zip-compressed'] },
    { extension: '.7z', mime: ['application/x-compressed', 'application/x-7z-compressed'] },
];

const UPLOAD_D0C_FILE_TYPE = [
    { extension: '.txt', mime: ['text/plain'] },
    { extension: '.text', mime: ['text/plain'] },
    { extension: '.doc', mime: ['application/msword'] },
    { extension: '.docx', mime: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
    { extension: '.xls', mime: ['application/vnd.ms-excel'] },
    { extension: '.xlsx', mime: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] },
    { extension: '.ppt', mime: ['application/vnd.ms-powerpoint'] },
    { extension: '.pptx', mime: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'] },
    { extension: '.csv', mime: ['text/csv', 'application/vnd.ms-excel'] },
    { extension: '.numbers', mime: ['', 'application/x-iwork-numbers-sffnumbers'] },
    { extension: '.pdf', mime: ['application/pdf'] },
    { extension: '.xsd', mime: ['', 'application/xml'] },
    { extension: '.pages', mime: ['', 'application/x-iwork-pages-sffpages'] },
    { extension: '.ab1', mime: ['', 'application/octet-stream'] },
    { extension: '.dna', mime: ['', 'application/octet-stream'] },
    { extension: '.fas', mime: ['', 'application/octet-stream'] },
    { extension: '.fasta', mime: ['', 'application/octet-stream'] },
    { extension: '.gb', mime: ['', 'application/octet-stream'] },
    { extension: '.sbd', mime: ['', 'application/octet-stream'] },
    { extension: '.seq', mime: ['', 'application/octet-stream'] },
    { extension: '.gbk', mime: ['', 'application/octet-stream'] },
    { extension: '.ape', mime: ['', 'application/octet-stream'] },
    { extension: '.rtf', mime: ['text/rtf', 'application/rtf', 'application/msword'] },
    ...WARNING_FILE_TYPE,
];

const UPLOAD_FILE_TYPE = [
    ...UPLOAD_D0C_FILE_TYPE,
    { extension: '.png', mime: ['image/png'] },
    { extension: '.jpg', mime: ['image/jpeg'] },
    { extension: '.jpeg', mime: ['image/jpeg'] },
];

const ABI_FILE_IMPORT_FILE_TYPE = [{ extension: '.txt', mime: ['text/plain'] }];

const MAX_SIZE_ALLOWED = 50 * 1024 * 1024;
const MAX_COUNT_ALLOWED = 1;

function validateFile(
    file: File,
    existingFileNames: UploadedFile[],
    type: FileUploadType
): { valid: boolean; validType: boolean; validSize: boolean; isUnique: boolean; warningType: boolean } {
    const validType = FileUploadHelper.validateFileType(file, type);
    const validSize = FileUploadHelper.validateFileSize(file);
    const isUnique = FileUploadHelper.validateUniqueFileName(file, existingFileNames);
    const warningType = FileUploadHelper.warningFileType(file);
    return { valid: validType && validSize && isUnique, validType, validSize, isUnique, warningType };
}

function validateUniqueFileName(file: File, existingFileNames: UploadedFile[]): boolean {
    return existingFileNames.findIndex(f => f.fileName === file.name) === -1;
}

function validateFileSize(file: File): boolean {
    return file.size <= MAX_SIZE_ALLOWED;
}

function validateBatchFileSize(files: File[]): boolean {
    const batchSize = files.map(f => f.size).reduce((a, b) => a + b, 0);
    return batchSize <= MAX_SIZE_ALLOWED;
}

function getAllowedFileTypes(type: FileUploadType) {
    if (type === FileUploadType.ABI_FILE_IMPORT) {
        return ABI_FILE_IMPORT_FILE_TYPE;
    }
    if (type === FileUploadType.PROBE_SEQUENCING) {
        return UPLOAD_D0C_FILE_TYPE;
    }
    return UPLOAD_FILE_TYPE;
}

function validateFileType(file: File, type: FileUploadType): boolean {
    const fileExtension = file.name.split('.').pop() || '';
    const fileType = file.type;
    return (
        FileUploadHelper.getAllowedFileTypes(type).findIndex(
            f => f.extension === `.${fileExtension}` && f.mime.findIndex(m => m === fileType) > -1
        ) > -1
    );
}

function warningFileType(file: File): boolean {
    const fileExtension = file.name.split('.').pop() || '';
    const fileType = file.type;
    return !(
        WARNING_FILE_TYPE.findIndex(
            f => f.extension === `.${fileExtension}` && f.mime.findIndex(m => m === fileType) > -1
        ) > -1
    );
}

function validateFileCount(existingFilesCount: number, newFilesCount: number): boolean {
    return existingFilesCount + newFilesCount <= MAX_COUNT_ALLOWED;
}

export const FileUploadHelper = {
    getAllowedFileTypes,
    validateFile,
    validateUniqueFileName,
    validateFileSize,
    validateBatchFileSize,
    validateFileType,
    warningFileType,
    validateFileCount,
    MAX_COUNT_ALLOWED,
};
